import type { DeliveryChallanDocument, DeliveryChallanLine, DeliveryChallanRow } from './types'
import { newDeliveryChallanLine } from './types'

/** v3: drop legacy v1/v2 on read so old seeded rows cannot reappear */
const STORAGE_KEY = 'erp_mock_delivery_challans_v3'
const LEGACY_STORAGE_KEYS = ['erp_mock_delivery_challans_v1', 'erp_mock_delivery_challans_v2'] as const

/** Static lookups until a real API exists */
export const MOCK_ITEMS = [
  { id: 101, name: 'Product A' },
  { id: 102, name: 'Product B' },
  { id: 103, name: 'Product C' },
]

export const MOCK_BATCHES_BY_ITEM: Record<string, Array<{ id: number; name: string; stock: number }>> = {
  '101': [
    { id: 1001, name: 'BATCH-A1', stock: 240 },
    { id: 1002, name: 'BATCH-A2', stock: 120 },
  ],
  '102': [{ id: 2001, name: 'BATCH-B1', stock: 500 }],
  '103': [
    { id: 3001, name: 'BATCH-C1', stock: 80 },
    { id: 3002, name: 'BATCH-C2', stock: 60 },
  ],
}

export function documentToRow(d: DeliveryChallanDocument): DeliveryChallanRow {
  return {
    id: d.id,
    code: d.code,
    customerName: d.customerName,
    orderCode: d.orderCode?.trim() || '—',
    challanDate: d.challanDate,
    expectedDeliveryDate: d.expectedDeliveryDate,
    createdDate: '',
    isInvoiceCreated: false,
  }
}

export function getAllChallans(): DeliveryChallanDocument[] {
  try {
    for (const key of LEGACY_STORAGE_KEYS) {
      localStorage.removeItem(key)
    }
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw != null && raw !== '') {
      const parsed = JSON.parse(raw) as unknown
      if (Array.isArray(parsed)) {
        return parsed.map((item) => coerceDocument(item as Partial<DeliveryChallanDocument> & Record<string, unknown>))
      }
    }
  } catch {
    /* ignore */
  }
  return []
}

export function saveAllChallans(docs: DeliveryChallanDocument[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(docs))
}

export function getChallanById(id: number): DeliveryChallanDocument | undefined {
  return getAllChallans().find((d) => d.id === id)
}

export function getNextChallanId(): number {
  const all = getAllChallans()
  return all.length === 0 ? 1 : Math.max(...all.map((d) => d.id)) + 1
}

export function getNextChallanCode(): string {
  const all = getAllChallans()
  const maxNum = all.reduce((m, d) => {
    const n = parseInt(d.code, 10)
    return Number.isFinite(n) ? Math.max(m, n) : m
  }, 0)
  return String(maxNum + 1).padStart(6, '0')
}

function normalizeLine(raw: unknown): DeliveryChallanLine {
  const def = newDeliveryChallanLine()
  if (!raw || typeof raw !== 'object') return def
  const r = raw as Record<string, unknown>
  const itemDisplayName =
    r.itemDisplayName != null && String(r.itemDisplayName).trim() !== ''
      ? String(r.itemDisplayName).trim()
      : undefined
  const batchDisplayName =
    r.batchDisplayName != null && String(r.batchDisplayName).trim() !== ''
      ? String(r.batchDisplayName).trim()
      : undefined
  const fromOrder = Boolean(r.fromOrder ?? r.FromOrder)
  return {
    id: Number(r.id ?? 0),
    itemId: String(r.itemId ?? ''),
    batchId: String(r.batchId ?? ''),
    availableQuantity: Number(r.availableQuantity ?? 0),
    quantityReceived: Number(r.quantityReceived ?? r.quantity ?? 0),
    quantityReturn: Number(r.quantityReturn ?? 0),
    ...(fromOrder ? { fromOrder: true } : {}),
    ...(itemDisplayName ? { itemDisplayName } : {}),
    ...(batchDisplayName ? { batchDisplayName } : {}),
  }
}

/** List total column: sum of received + return quantities */
export function computeDocumentTotal(lines: DeliveryChallanLine[]): number {
  return lines.reduce(
    (sum, l) => sum + Number(l.quantityReceived || 0) + Number(l.quantityReturn || 0),
    0,
  )
}

export function upsertChallan(doc: DeliveryChallanDocument): DeliveryChallanDocument {
  const all = getAllChallans()
  let working = { ...doc }
  if (!working.id || working.id === 0) {
    working = {
      ...working,
      id: getNextChallanId(),
      code: getNextChallanCode(),
    }
  }
  const idx = all.findIndex((d) => d.id === working.id)
  const nextTotal = computeDocumentTotal(working.lines)
  const normalized: DeliveryChallanDocument = { ...working, total: nextTotal }
  if (idx >= 0) {
    all[idx] = normalized
  } else {
    all.push(normalized)
  }
  saveAllChallans(all)
  return normalized
}

export function defaultNewDocument(): DeliveryChallanDocument {
  return {
    id: 0,
    code: '',
    orderId: '',
    orderCode: '',
    orderReference: '',
    orderWarehouseName: '',
    orderStatus: '',
    customerId: '',
    customerName: '',
    challanDate: new Date().toISOString().slice(0, 10),
    expectedDeliveryDate: new Date().toISOString().slice(0, 10),
    attachmentFileName: '',
    attachmentFilePath: '',
    total: 0,
    lines: Array.from({ length: 3 }, () => newDeliveryChallanLine()),
  }
}

function coerceDocument(raw: Partial<DeliveryChallanDocument> & Record<string, unknown>): DeliveryChallanDocument {
  const def = defaultNewDocument()
  const linesRaw = raw.lines
  const lines =
    Array.isArray(linesRaw) && linesRaw.length > 0 ? linesRaw.map((x) => normalizeLine(x)) : def.lines
  return {
    ...def,
    ...raw,
    id: Number(raw.id) || 0,
    code: String(raw.code ?? def.code),
    orderId: raw.orderId !== undefined && raw.orderId !== null ? String(raw.orderId) : '',
    orderCode: String(raw.orderCode ?? ''),
    orderReference: String(raw.orderReference ?? ''),
    orderWarehouseName: String(raw.orderWarehouseName ?? ''),
    orderStatus: String(raw.orderStatus ?? ''),
    customerId: String(raw.customerId ?? ''),
    customerName: String(raw.customerName ?? ''),
    challanDate: String(raw.challanDate ?? def.challanDate),
    expectedDeliveryDate: String(raw.expectedDeliveryDate ?? def.expectedDeliveryDate),
    attachmentFileName: String(raw.attachmentFileName ?? ''),
    attachmentFilePath: String(
      (raw as { attachmentFilePath?: unknown })?.attachmentFilePath ?? '',
    ),
    total: Number(raw.total) || 0,
    lines,
  }
}

/**
 * Example JSON shape for backend integration later:
 * { id, code, orderId, orderReference, orderWarehouseName, orderStatus, ... }
 */
export const MOCK_DELIVERY_CHALLAN_JSON_EXAMPLE = `{
  "id": 1,
  "code": "000001",
  "orderId": 1,
  "orderCode": "000001",
  "orderReference": "433",
  "orderWarehouseName": "warehouse 1",
  "orderStatus": "Draft",
  "customerId": 1,
  "customerName": "Acme Trading LLC",
  "challanDate": "2026-04-04",
  "expectedDeliveryDate": "2026-04-08",
  "attachmentFileName": "challan-scan.pdf",
  "total": 1150,
  "lines": [
    {
      "id": 1,
      "itemId": 101,
      "batchId": 1001,
      "availableQuantity": 240,
      "quantityReceived": 10,
      "quantityReturn": 0
    }
  ]
}`
